// src/pages/MyTraining.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { CoursePreview } from "./CourseDesigner";

const PRIMARY = "#1F2937";

// ── Helpers ───────────────────────────────────────────────────────────────────
function getMandatory(course, progress) {
  // Per-user assignment flag takes precedence
  if (progress?.mandatory != null) return progress.mandatory;
  // Course-level default (only meaningful for available_to_all)
  if (course.availabilityMode === "available_to_all") return course.mandatoryMode === "mandatory";
  // assigned_only without per-user flag = optional by default
  return false;
}

function getDueDate(course, progress, mandatory) {
  if (!mandatory)              return null; // optional — no renewal ever
  if (course.frequencyDays === 0) return null; // ad-hoc mandatory — complete once
  if (!progress?.completedAt) return null; // not yet completed
  const d = new Date(progress.completedAt);
  d.setDate(d.getDate() + course.frequencyDays);
  return d;
}

function getOverdue(course, progress, mandatory) {
  if (!mandatory)              return false; // optional courses are never overdue
  if (!progress?.completedAt) return course.frequencyDays !== 0; // mandatory, never done, not ad-hoc = overdue
  if (course.frequencyDays === 0) return false; // ad-hoc mandatory — once done, never overdue
  const due = getDueDate(course, progress, mandatory);
  return due && due < new Date();
}

// ── Course section ────────────────────────────────────────────────────────────
function CourseSection({ title, courses, progress, onLaunch, accent }) {
  if (!courses.length) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
        <div style={{ width:4, height:20, borderRadius:2, background:accent, flexShrink:0 }}/>
        <h2 style={{ fontSize:15, fontWeight:700, color:"var(--gray-700)" }}>{title}</h2>
        <span style={{ fontSize:13, color:"var(--gray-400)" }}>({courses.length})</span>
      </div>
      <div className="courses-list">
        {courses.map(c => (
          <CourseCard key={c.id} course={c} progress={progress[c.id]} onLaunch={onLaunch}/>
        ))}
      </div>
    </div>
  );
}

// ── Course card ───────────────────────────────────────────────────────────────
function CourseCard({ course, progress, onLaunch }) {
  const mandatory = getMandatory(course, progress);
  const dueDate   = getDueDate(course, progress, mandatory);
  const overdue   = getOverdue(course, progress, mandatory);
  const completed = progress?.status === "completed";
  const isTest    = course.status === "test";

  // Due date display — optional courses never show renewal info
  let dueLine = null;
  if (!isTest && mandatory) {
    if (course.frequencyDays === 0) {
      // Ad-hoc mandatory — once done, no renewal
      dueLine = completed
        ? <span style={{fontSize:12,color:"var(--gray-500)"}}>One-off — no renewal required</span>
        : <span style={{fontSize:12,color:"var(--gray-500)"}}>One-off — complete once</span>;
    } else if (!completed) {
      dueLine = <span style={{fontSize:12,color:"var(--red)",fontWeight:600}}>⚠ Overdue — not yet completed</span>;
    } else if (overdue) {
      dueLine = <span style={{fontSize:12,color:"var(--red)",fontWeight:600}}>⚠ Renewal overdue since {dueDate.toLocaleDateString("en-GB")}</span>;
    } else if (dueDate) {
      dueLine = <span style={{fontSize:12,color:"var(--gray-500)"}}>Renewal due: {dueDate.toLocaleDateString("en-GB")}</span>;
    }
  }

  // Section completion percentage (for in-progress courses with sections)
  const sectionPct = (() => {
    if (completed || !progress || progress.status === "not_started") return null;
    // sections come from progress.sectionProgress keys - we don't have the design loaded here
    const sp = progress.sectionProgress;
    if (!sp || Object.keys(sp).length === 0) return null;
    // We can show completed count but not total without the design
    // Use the total from progress if available, otherwise just show completed count
    const doneCount = Object.values(sp).filter(v => v === "complete").length;
    if (doneCount === 0) return null;
    return { done: doneCount, total: null, pct: null }; // total unknown without design
  })();

  return (
    <div className="course-card" style={{
      border: isTest ? "1.5px solid #F59E0B" : overdue ? "1.5px solid #FCA5A5" : "1px solid var(--gray-200)",
      background: isTest ? "#FFFBEB" : undefined,
    }}>
      <div style={{ flex:1, minWidth:0 }}>

        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:6 }}>
          <div style={{ fontWeight:600, fontSize:15, color:"var(--gray-900)" }}>{course.name}</div>
          <div style={{ display:"flex", gap:6, alignItems:"center", flexShrink:0, flexWrap:"wrap", justifyContent:"flex-end" }}>
            {isTest && (
              <span style={{ fontSize:11, fontWeight:700, background:"#FEF3C7", color:"#92400E", padding:"2px 8px", borderRadius:4, textTransform:"uppercase", letterSpacing:"0.05em", border:"1px solid #F59E0B" }}>
                🧪 Test
              </span>
            )}
            {course.durationMinutes && (
              <span style={{ fontSize:12, color:"var(--gray-500)", background:"var(--gray-100)", padding:"2px 8px", borderRadius:99 }}>
                ⏱ {course.durationMinutes} min
              </span>
            )}
            <span className={`badge ${mandatory ? "badge-overdue" : "badge-neutral"}`} style={{ fontSize:11 }}>
              {mandatory ? "Mandatory" : "Optional"}
            </span>
          </div>
        </div>

        {course.description && (
          <div style={{ fontSize:13, color:"var(--gray-500)", marginBottom:8, lineHeight:1.5 }}>{course.description}</div>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          {/* Completion status */}
          {completed
            ? <span className="badge badge-ok" style={{ fontSize:11 }}>✓ Completed</span>
            : progress?.status === "in_progress"
              ? <span className="badge badge-due" style={{ fontSize:11 }}>In Progress</span>
              : <span className="badge badge-neutral" style={{ fontSize:11 }}>Not Started</span>
          }
          {/* Section progress percentage */}
          {sectionPct && (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, color:"var(--gray-600)", fontWeight:600 }}>
                {sectionPct.done} section{sectionPct.done !== 1 ? "s" : ""} complete
              </span>
            </div>
          )}
          {/* Score */}
          {progress?.score != null && (
            <span style={{ fontSize:12, color:"var(--gray-600)", fontWeight:500 }}>Score: {progress.score}%</span>
          )}
          {/* Completion date */}
          {progress?.completedAt && (
            <span style={{ fontSize:12, color:"var(--gray-500)" }}>Completed: {new Date(progress.completedAt).toLocaleDateString("en-GB")}</span>
          )}
          {/* Due line — not shown for test courses */}
          {!isTest && dueLine}
          {/* Test note */}
          {isTest && (
            <span style={{ fontSize:12, color:"#92400E" }}>Progress not recorded in test mode</span>
          )}
        </div>
      </div>

      <div style={{ display:"flex", flexShrink:0, gap:8, alignItems:"center" }}>
        {/* Certificate download — shown for completed courses that have certificate enabled */}
        {completed && course.enableCertificate && (
          <a
            href={`/api/courses/${course.id}/certificate`}
            download={`certificate-${course.name.replace(/[^a-z0-9]/gi,"-").toLowerCase()}.pdf`}
            style={{ flexShrink:0 }}
          >
            <button className="btn-secondary" style={{ fontSize:13, padding:"8px 14px", whiteSpace:"nowrap" }}>
              🏅 Certificate
            </button>
          </a>
        )}
        <button
          className="btn-primary"
          style={{ flexShrink:0, fontSize:13, padding:"8px 18px", background: isTest ? "#D97706" : overdue ? "#DC2626" : PRIMARY }}
          onClick={() => onLaunch(course)}
          disabled={!course.hasDesign}
        >
          {isTest ? "Preview"
            : completed ? "Retake"
            : progress?.status === "in_progress" ? "Continue"
            : "Start"}
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MyTraining() {
  const { user }   = useAuth();
  const [courses,  setCourses]  = useState([]);
  const [progress, setProgress] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [active,   setActive]   = useState(null);

  const [activeDesign, setActiveDesign] = useState(null);
  const [loadingDesign, setLoadingDesign] = useState(false);

  async function launchCourse(course) {
    setLoadingDesign(true);
    try {
      const r = await fetch(`/api/courses/${course.id}/design`);
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const d = await r.json();
      const design = d.courseDesign;
      if (!design || !Array.isArray(design.pages) || design.pages.length === 0) {
        alert("This course doesn't have any content yet. Please contact your administrator.");
        return;
      }
      setActiveDesign(design);
      setActive(course);
    } catch (e) {
      console.error("launchCourse error:", e);
      alert("Could not load course content. Please try again or contact your administrator.");
    } finally {
      setLoadingDesign(false);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const c = await fetch("/api/courses").then(r => r.json());
      const filtered = (c.courses||[]).filter(c => {
        if (c.status === "live") return true;
        if (c.status === "test" && (user?.role === "admin" || user?.role === "supervisor")) return true;
        return false;
      });
      setCourses(filtered);
      const prog = {};
      await Promise.all(filtered.map(async c => {
        const p = await fetch(`/api/progress/${c.id}`).then(r => r.json()).catch(() => null);
        if (p) prog[c.id] = p;
      }));
      setProgress(prog);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Categorise into three buckets
  const testCourses       = courses.filter(c => c.status === "test");
  const liveCourses       = courses.filter(c => c.status === "live");

  const mandatoryIncomplete = liveCourses.filter(c => {
    const mandatory  = getMandatory(c, progress[c.id]);
    const completed  = progress[c.id]?.status === "completed";
    const dueDate    = getDueDate(c, progress[c.id], getMandatory(c, progress[c.id]));
    const needRenew  = completed && dueDate && dueDate < new Date();
    return mandatory && (!completed || needRenew);
  }).sort((a,b) => {
    // Overdue first, then alphabetical
    const ao = getOverdue(a, progress[a.id], getMandatory(a, progress[a.id]));
    const bo = getOverdue(b, progress[b.id], getMandatory(b, progress[b.id]));
    if (ao !== bo) return ao ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  const optionalIncomplete = liveCourses.filter(c => {
    const mandatory = getMandatory(c, progress[c.id]);
    const completed = progress[c.id]?.status === "completed";
    const dueDate   = getDueDate(c, progress[c.id], getMandatory(c, progress[c.id]));
    const needRenew = completed && dueDate && dueDate < new Date();
    return !mandatory && (!completed || needRenew);
  }).sort((a,b) => a.name.localeCompare(b.name));

  const completedCurrent = liveCourses.filter(c => {
    const completed = progress[c.id]?.status === "completed";
    const dueDate   = getDueDate(c, progress[c.id], getMandatory(c, progress[c.id]));
    const needRenew = dueDate && dueDate < new Date();
    return completed && !needRenew;
  }).sort((a,b) => {
    // Most recently completed first
    const da = progress[a.id]?.completedAt || "";
    const db = progress[b.id]?.completedAt || "";
    return db.localeCompare(da);
  });

  if (loading) return <div className="page-loading"><span className="spinner" style={{ width:28, height:28 }}/></div>;

  const totalLive      = liveCourses.length;
  const totalCompleted = completedCurrent.length;
  const totalMandatory = liveCourses.filter(c => getMandatory(c, progress[c.id])).length;
  const mandatoryDone  = liveCourses.filter(c => getMandatory(c, progress[c.id]) && progress[c.id]?.status === "completed" && !getOverdue(c, progress[c.id], getMandatory(c, progress[c.id]))).length;

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">My Training</div><div className="page-subtitle">Your assigned and available training courses</div></div>
      </div>

      {/* Summary stats */}
      {totalLive > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:24 }}>
          {[
            { label:"Mandatory Complete", value:`${mandatoryDone} / ${totalMandatory}`, colour: mandatoryDone < totalMandatory ? "#DC2626" : "#10B981" },
            { label:"Total Complete",     value:`${totalCompleted} / ${totalLive}`,     colour: "#1F2937" },
            { label:"Overdue",            value: String(mandatoryIncomplete.filter(c => getOverdue(c, progress[c.id], getMandatory(c, progress[c.id]))).length), colour:"#DC2626" },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding:"14px 20px" }}>
              <div style={{ fontSize:26, fontWeight:700, color:s.colour, marginBottom:4 }}>{s.value}</div>
              <div style={{ fontSize:13, color:"var(--gray-500)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {courses.length === 0 ? (
        <div className="card"><div className="empty-state"><h3>No courses available</h3><p>Training courses will appear here once assigned.</p></div></div>
      ) : (
        <>
          {/* Test courses — admins/supervisors only */}
          {testCourses.length > 0 && (
            <CourseSection title="Test Previews" courses={testCourses} progress={progress} onLaunch={launchCourse} accent="#F59E0B"/>
          )}
          <CourseSection title="Mandatory — Action Required" courses={mandatoryIncomplete} progress={progress} onLaunch={launchCourse} accent="#DC2626"/>
          <CourseSection title="Optional — Not Yet Completed"  courses={optionalIncomplete}  progress={progress} onLaunch={launchCourse} accent="#6B7280"/>
          <CourseSection title="Completed"                     courses={completedCurrent}    progress={progress} onLaunch={launchCourse} accent="#10B981"/>
        </>
      )}

      {loadingDesign && (
        <div className="pdf-overlay" style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
          <span className="spinner" style={{ width:36, height:36 }}/>
        </div>
      )}
      {active && activeDesign && (
        <CoursePreview design={activeDesign} completionMode={active.completionMode||"last_page"} courseId={active.id}
          userName={user?.name || user?.email}
          courseName={active.name}
          onClose={() => { setActive(null); setActiveDesign(null); }} onComplete={() => { setActive(null); setActiveDesign(null); load(); }}/>
      )}
    </div>
  );
}
